import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiCellphone, mdiEmailOutline, mdiMessageOutline, mdiPencilOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { displaySender } from '../conversations/ConversationsPage'
import { threadTitleT } from '../conversations/thread'
import type { Conversation } from '../../api/schema'

/**
 * "Tell the people who asked" — the threads a closed ticket came from, and the
 * one gesture that answers each of them (spec 2026-09-16-09).
 *
 * A promotion is a LINK, not a copy (`conversation_issues`): the thread stayed
 * open precisely so the resident could be answered in it. Closing the ticket
 * used to be a dead end for that thread — the issue page knew nothing about it,
 * and nothing invited the manager back.
 *
 * It OFFERS, it never does: closing a ticket sends nothing and enqueues
 * nothing. Each row opens its own thread with the composer pre-filled, and the
 * send stays the manager's own gesture, in the thread. One action per thread —
 * no broadcast — because each thread has its own history and its own contact,
 * and the same sentence sent to people who asked different questions is worse
 * than silence.
 */

const CHANNEL_ICON = { email: mdiEmailOutline, whatsapp: mdiCellphone, web: mdiMessageOutline } as const

/** The statuses on which a ticket owes its threads an answer. `resolved` is
 * not in the shipped status list today; it is accepted here so that a type
 * that ever carries it does not silently lose the section. */
const ANSWERABLE_STATUSES = new Set(['closed', 'resolved'])

/**
 * Has the resident been told since the close?
 *
 * A message went OUT after `closed_at`. An inbound after the close is the
 * resident writing again, not the resident being told, so it deliberately does
 * not count — that thread is exactly the one still owed an answer.
 */
function toldSinceClose(row: Conversation, closedAt: string | null | undefined): boolean {
    // No stamp at all — a ticket closed before `closed_at` was written on the
    // console's own status change — reads as NOT told. An invitation shown one
    // time too many costs a manager a glance; one never shown leaves a
    // resident waiting, which is the whole point of this section.
    if (!closedAt || !row.last_outbound_at) return false
    const closed = new Date(closedAt).getTime()
    const answered = new Date(row.last_outbound_at).getTime()
    if (Number.isNaN(closed) || Number.isNaN(answered)) return false

    return answered > closed
}

export function ClosedTicketThreads({
    org,
    issueUid,
    status,
    closedAt,
}: {
    org: string
    issueUid: string
    status: string | undefined
    closedAt: string | null | undefined
}) {
    const { t } = useTranslation('issues')
    const answerable = ANSWERABLE_STATUSES.has(String(status ?? ''))

    const { data } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/conversations',
        {
            params: {
                path: { orgCode: org },
                query: {
                    issue: issueUid,
                    status: 'open,waiting_resident,closed',
                    limit: 20,
                },
            },
        },
        { enabled: answerable },
    )

    const rows = ((data as { data?: Conversation[] } | undefined)?.data ?? []) as Conversation[]
    if (!answerable || rows.length === 0) return null

    return (
        <Card data-testid="issue-closed-threads-card">
            <CardContent className="py-4 space-y-2">
                <h2 className="text-sm font-semibold text-foreground">{t('closed_threads.title')}</h2>
                <p className="text-xs text-muted-foreground" data-testid="issue-closed-threads-lead">
                    {t(rows.length > 1 ? 'closed_threads.lead_plural' : 'closed_threads.lead', {
                        threads: rows.length,
                    })}
                </p>
                <ul className="divide-y border rounded-lg" data-testid="issue-closed-threads">
                    {rows.map((row) => {
                        const channelIcon =
                            CHANNEL_ICON[(row.channel ?? 'email') as keyof typeof CHANNEL_ICON] ?? mdiEmailOutline
                        const told = toldSinceClose(row, closedAt)

                        return (
                            <li
                                key={row.uid}
                                className="flex items-start gap-2 px-3 py-2"
                                data-testid={`issue-closed-thread-${row.uid}`}
                                data-told={told ? 'yes' : 'no'}
                            >
                                <AppIcon path={channelIcon} size="xs" className="mt-1 flex-shrink-0" />
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm text-foreground truncate">
                                        {threadTitleT(row, t, 'conversations:queue.no_subject')}
                                    </span>
                                    <span className="block text-[11px] text-muted-foreground truncate">
                                        {displaySender(row, t('closed_threads.anonymous'))}
                                    </span>
                                </span>
                                <Badge
                                    variant="outline"
                                    className={`text-[10px] flex-shrink-0 ${
                                        told
                                            ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20'
                                            : ''
                                    }`}
                                    data-testid={`issue-closed-thread-state-${row.uid}`}
                                >
                                    {told ? t('closed_threads.told') : t('closed_threads.not_told')}
                                </Badge>
                                <Button
                                    asChild
                                    variant="outline"
                                    size="sm"
                                    className="flex-shrink-0"
                                    data-testid={`issue-closed-thread-write-${row.uid}`}
                                >
                                    <Link
                                        to="/$org/conversations"
                                        params={{ org }}
                                        search={{
                                            conversation: row.uid,
                                            status: 'open,waiting_resident,closed',
                                            reply_for_issue: issueUid,
                                            channel: undefined,
                                            resolution: undefined,
                                            asset: undefined,
                                            unattached: undefined,
                                            draft: undefined,
                                            q: undefined,
                                            selected: undefined,
                                            message: undefined,
                                        }}
                                    >
                                        <AppIcon path={mdiPencilOutline} size="xs" className="md:mr-1" />
                                        <span className="hidden md:inline">
                                            {told
                                                ? t('closed_threads.action_again')
                                                : t('closed_threads.action')}
                                        </span>
                                    </Link>
                                </Button>
                            </li>
                        )
                    })}
                </ul>
            </CardContent>
        </Card>
    )
}
